import { playWordInSentence } from '@/common/utils/audio';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import Duelist from '@/game/GameComponents/PhaserGame/Dueling/Duelist';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  biomeKeys,
  getAnchorPlugin,
  getBiomeFromStudentLevel,
  waitForSeconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { RewardsManager } from '@/game/GameComponents/PhaserGame/Utils/RewardsManager';
import { SpellID } from '@/game/prefabs/Duel/SpellIcon';
import TurnIndicator from '@/game/prefabs/Duel/TurnIndicator';
import NPC from '@/game/prefabs/World/NPC';
import Player from '@/game/prefabs/World/Player';
import DuelUI from '@/game/scenes/DuelScenes/DuelUI';
import DuelWordCreationPanel from '@/game/scenes/DuelScenes/DuelWordCreationPanel';
import RiposteUI from '@/game/scenes/DuelScenes/RiposteUI';
import LevelMap, { getTilemapLevel } from '@/game/scenes/LevelMap';
/* START OF COMPILED CODE */

export default class Duel extends Phaser.Scene {
  constructor() {
    super('Duel');

    /* START-USER-CTR-CODE */
    /* END-USER-CTR-CODE */
  }

  editorCreate(): void {
    // duelArena
    const duelArena = this.add.container(0, 0);

    // arena
    const arena = this.add.sprite(0, 0, 'battle_map_forest');
    arena.scaleX = 4;
    arena.scaleY = 4;
    arena.setOrigin(0, 0);
    duelArena.add(arena);

    // duelistHolder
    const duelistHolder = this.add.container(512, 384);

    // turnIndicator
    const turnIndicator = new TurnIndicator(this, -220, 0);
    duelistHolder.add(turnIndicator);

    // duelist_enemy
    const duelist_enemy = new NPC(this, 220, 0);
    duelist_enemy.scaleX = 3;
    duelist_enemy.scaleY = 3;
    duelistHolder.add(duelist_enemy);

    // player
    const player = new Player(this, -220, 0);
    player.scaleX = 3;
    player.scaleY = 3;
    duelistHolder.add(player);

    // duelCurtain
    const duelCurtain = this.add.image(0, 0, '1x1sprite');
    duelCurtain.scaleX = 1600;
    duelCurtain.scaleY = 1024;
    duelCurtain.setOrigin(0, 0);
    duelCurtain.visible = false;
    duelCurtain.tintTopLeft = 0;
    duelCurtain.tintTopRight = 0;
    duelCurtain.tintBottomLeft = 0;
    duelCurtain.tintBottomRight = 0;

    // duelist_enemy (prefab fields)
    duelist_enemy.npcId = 3;
    duelist_enemy.npcLevelId = 0;

    // player (prefab fields)
    player.interactionEnabled = false;

    this.arena = arena;
    this.duelArena = duelArena;
    this.turnIndicator = turnIndicator;
    this.duelist_enemy = duelist_enemy;
    this.player = player;
    this.duelistHolder = duelistHolder;
    this.duelCurtain = duelCurtain;

    this.events.emit('scene-awake');
  }

  private arena!: Phaser.GameObjects.Sprite;
  private duelArena!: Phaser.GameObjects.Container;
  private turnIndicator!: TurnIndicator;
  private duelist_enemy!: NPC;
  private player!: Player;
  private duelistHolder!: Phaser.GameObjects.Container;
  private duelCurtain!: Phaser.GameObjects.Image;

  /* START-USER-CODE */
  public activeDuelistIndex: number = -1;
  public duelists: Duelist[] = [];
  public duelIsFinished: boolean = false;

  getWordCreationPanel(): DuelWordCreationPanel {
    return this.scene.get('DuelWordCreationPanel') as DuelWordCreationPanel;
  }

  getDuelUI(): DuelUI {
    return this.scene.get('DuelUI') as DuelUI;
  }

  getRiposteUI(): RiposteUI {
    return this.scene.get('RiposteUI') as RiposteUI;
  }

  preload(): void {
    // Preload the battle map for this duel.
    const biomeIdx = getBiomeFromStudentLevel(getStudent().level);
    const currentBiome = `${biomeKeys[biomeIdx]}`;
    this.load.image(
      `duel-arena-${currentBiome}`,
      `game_assets/sprites/phaser_game/duel_arenas/duel-arena-${currentBiome}.png`,
    );
    // Preload audio
    this.load.pack('duel-audio', 'game_assets/audio/duel-audio.json');
    this.load.pack('minigame-sfx', 'game_assets/audio/sfx/minigame-sfx.json');

    // Preload spell effects.
    this.load.pack('vfx', 'game_assets/vfx/vfx.json');
  }

  async create(data: any) {
    this.editorCreate();
    this.duelCurtain.setVisible(true);
    const duelUI = this.getDuelUI();
    // Wait until the duel UI scene has started.
    await waitUntil(this.time, () => this.scene.isActive('DuelUI'), 60);
    const student = getStudent();

    // Create the arena.
    this.arena.setTexture(
      `duel-arena-${
        getTilemapLevel(student.id, student.mapsCompleted, student.level).biome
      }`,
    );

    const duelist = this.duelist_enemy;
    // Copy the data from the node's NPC.
    duelist.npcLevelId = data.node.nPC.npcLevelId;
    duelist.npcId = data.node.nPC.npcId;
    duelist.configure(data.node.nPC.configuration);

    duelUI.endDuelUI.setVisible(false);

    const playerDuelist = new Duelist({
      maxHP: 5,
      avatar: this.player!,
      isActive: true,
      index: 0,
    });
    this.duelists.push(playerDuelist);

    const enemyDuelist = new Duelist({
      maxHP: 3,
      avatar: this.duelist_enemy,
      isActive: false,
      index: 1,
    });
    this.duelists.push(enemyDuelist);

    duelUI.healthBarPlayer.awake({
      duelist: playerDuelist,
      duelistIndex: 0,
    });

    duelUI.healthBarEnemy.awake({
      duelist: enemyDuelist,
      duelistIndex: 1,
    });

    // Duel-specific animations.
    if (!this.anims.get('explosion-orange')) {
      this.anims.create({
        key: 'explosion-orange',
        frames: this.anims.generateFrameNumbers('explosion-orange'),
        frameRate: 12,
        repeat: 0,
      });
    }
    if (!this.anims.get('iceblast')) {
      this.anims.create({
        key: 'iceblast',
        frames: this.anims.generateFrameNumbers('iceblast'),
        frameRate: 12,
        repeat: 0,
      });
    }
    if (!this.anims.get('arcaneblast')) {
      this.anims.create({
        key: 'arcaneblast',
        frames: this.anims.generateFrameNumbers('arcaneblast'),
        frameRate: 12,
        repeat: 0,
      });
    }

    // Show the duel entry screen.
    duelUI.duelEntryScreen.setVisible(true);
    duelUI.duelEntryScreen.nPC.npcLevelId = data.node.nPC.npcLevelId;
    duelUI.duelEntryScreen.nPC.npcId = data.node.nPC.npcId;
    duelUI.duelEntryScreen.nPC.configure(data.node.nPC.configuration);
    //this.scene.bringToTop('DuelWalkthroughPanel');
    this.scene.bringToTop('DuelWordCreationPanel');
    this.scene.bringToTop('DuelUI');
    this.scene.bringToTop('RiposteUI');

    this.getDuelUI().scene.setVisible(true);
    getSceneManager()?.pauseScene('RiposteUI');
    await waitForSeconds(this.time, 2);
    duelUI.duelEntryScreen.setVisible(false);
    // Hide the duel curtain.
    this.tweens.add({
      targets: this.duelCurtain,
      alpha: 0,
      duration: 500,
      ease: 'Linear',
    });
    this.getWordCreationPanel().scene.setVisible(true);
    // Duel Walkthrough.
    duelUI.duelWalkthroughPanel.setVisible(false);

    // Referee
    duelUI.refereeContainer.setVisible(false);

    // Camera setup
    this.updateArenaScale();
    ResponsivenessManager.InitializeResizeHandler(
      this,
      () => this.updateArenaScale(),
      this,
    );

    // Responsiveness.
    const anchor = getAnchorPlugin(this);

    anchor.add(this.duelistHolder, {
      centerY: '50%',
      centerX: '50%',
    });

    // Event listeners.
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.WORD_VERIFICATION_FINISHED,
      this.handleWordVerificationFinished,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.WORD_WAS_PLAYED,
      this.handleWordWasPlayed,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.DUELIST_WAS_DEFEATED,
      this.handleDuelistWasDefeated,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.DUELIST_PERFORMED_RIPOSTE,
      this.handleDuelistPerformedRiposte,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.SETTINGS_PANEL_CLOSED,
      this.handleSettingsPanelClosed,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.SETTINGS_PANEL_CLOSED,
      this.handleSettingsPanelClosed,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.PLAYER_QUIT_DUEL,
      this.handlePlayerQuitDuel,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.PLAYER_REQUESTED_DUEL_WALKTHROUGH,
      this.handlePlayerRequestedDuelWalkthrough,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.NEW_DUEL_WORD_STARTED,
      this.handleNewDuelWordStarted,
      this,
    );
    this.events.on('shutdown', this.shutdown, this);
    this.events.on('prerender', this.handlePrerender, this);

    EventBus.emit(GAME_EVENTS.DUEL_STARTED);

    // Track duel started event
    trackMixpanelEvent('Duel Started', {
      enemyNpcId: this.duelist_enemy.npcId,
      enemyNpcLevelId: this.duelist_enemy.npcLevelId,
    });

    // Show walkthrough if this is their first time in a duel.
    if (!getStudent().tutorialization.hasViewedDuelWalkthrough) {
      duelUI.duelWalkthroughPanel.setVisible(true);
      EventBus.emit(GAME_EVENTS.PLAYER_VIEWED_DUEL_WALKTHROUGH);
      EventsManager.registerEventOnce(
        this,
        GAME_EVENTS.WALKTHROUGH_COMPLETED,
        () => {
          this.switchTurn();
        },
        this,
      );

      // Track duel walkthrough viewed event
      trackMixpanelEvent('Duel Walkthrough Viewed', {
        enemyNpcId: this.duelist_enemy.npcId,
        enemyNpcLevelId: this.duelist_enemy.npcLevelId,
      });
    } else {
      // await waitForSeconds(this.time, 2);
      // duelUI.duelEntryScreen.setVisible(false);
      await waitForSeconds(this.time, 1);
      this.switchTurn(); // start the first duelist's turn.
    }
  }

  switchTurn() {
    // cycle to the next available duelist.
    this.activeDuelistIndex++;

    this.activeDuelistIndex = this.activeDuelistIndex % this.duelists.length;
    EventBus.emit(GAME_EVENTS.SWITCH_TURN);
    switch (this.activeDuelistIndex) {
      case 0:
        EventBus.emit(GAME_EVENTS.SET_ACTIVE, this.duelists[0]);
        break;
      default:
        EventBus.emit(GAME_EVENTS.SET_ACTIVE, this.duelists[1]);
        EventBus.emit(GAME_EVENTS.PLAY_AI_WORD);
        break;
    }
  }

  updateArenaScale() {
    if (!this.cameras.main) return;
    const { arena } = this;
    const widthDiff =
      this.duelArena.scaleX * arena.width * arena.scaleX -
      this.game.scale.width;
    const heightDiff =
      this.duelArena.scaleX * arena.height * arena.scaleY -
      this.game.scale.height;
    if (widthDiff < heightDiff) {
      this.duelArena.setScale(
        this.game.scale.width / (arena.width * arena.scaleX),
      );
    } else {
      this.duelArena.setScale(
        this.game.scale.height / (arena.height * arena.scaleY),
      );
    }

    // Update duelist positions.
    // const centerY = this.game.scale.height / 2;
    // const duelistYOffset = -100 * this.duelArena.scaleY;
    // this.player.setY(centerY + duelistYOffset);
    // this.duelist_enemy.setY(centerY + duelistYOffset);
  }

  async handleWordVerificationFinished() {
    await this.waitForNextTurnToBegin();
    if (!this.duelIsFinished) this.switchTurn();
  }

  async waitForNextTurnToBegin() {
    let nextTurnCanBegin = false;
    let waitedForRiposte = false;
    // This is where it would be great to have a state machine.
    if (!this.getRiposteUI().isRiposting()) {
      nextTurnCanBegin = true;
    } else {
      waitedForRiposte = true;
      EventsManager.registerEventOnce(
        this,
        GAME_EVENTS.DUELIST_ATTEMPTED_RIPOSTE,
        () => {
          nextTurnCanBegin = true;
        },
        this,
      );
    }

    await waitUntil(this.time, () => nextTurnCanBegin, Number.MAX_SAFE_INTEGER);
    const endDelay = waitedForRiposte ? 2 : 0.2;
    await waitForSeconds(this.time, endDelay);
  }

  handleWordWasPlayed(payload: any) {
    // Track word played event
    trackMixpanelEvent('Word Played in Duel', {
      word: payload.word,
      wordWasValid: payload.wordWasValid,
      duelistIndex: payload.duelistIndex,
      isPlayerTurn: payload.duelistIndex === 0,
    });

    // Prompt the duelist who played the word to cast a spell.
    if (payload.wordWasValid) {
      const { duelistIndex } = payload;

      // Cast spell (animate)
      this.castSpell(duelistIndex, payload.word).then(() => {
        for (var i = 0; i < this.duelists.length; i++) {
          if (i !== duelistIndex) {
            this.duelists[i].modifyCurrentHP(-1); // todo: increase damage based on word complexity.
          }
        }

        if (!this.duelIsFinished) {
          this.startVerificationTimeout();
        }
      });
    } else {
      if (!this.duelIsFinished) {
        this.startVerificationTimeout();
      }
    }
  }

  async startVerificationTimeout() {
    await waitForSeconds(this.time, 2);
    EventBus.emit(GAME_EVENTS.WORD_VERIFICATION_FINISHED);
  }

  castSpell(duelistIndex: number, word: string): Promise<void> {
    return new Promise(async (resolve) => {
      const duelist = this.duelists[duelistIndex];
      const spellId =
        this.getDuelUI().spellSelectionUI.getCurrentSpellID() ||
        SpellID.fireball;

      // Track spell cast event
      trackMixpanelEvent('Spell Cast in Duel', {
        word: word,
        spellId: spellId,
        duelistIndex: duelistIndex,
        isPlayerTurn: duelistIndex === 0,
      });

      await duelist.castSpell(this, spellId);
      resolve();
    });
  }

  handleDuelistWasDefeated(payload: any) {
    const { duelist } = payload;

    this.duelIsFinished = true;
    PhaserAudioManager.setMusicPlaying(false);
    waitForSeconds(this.time, 1).then(() => {
      PhaserAudioManager.playSFX(this, 'duelist-defeated');
    });

    // Track duelist defeated event
    trackMixpanelEvent('Duelist Defeated', {
      defeatedDuelistIndex: duelist.index,
      isPlayerDefeated: duelist === this.duelists[0],
      enemyNpcId: this.duelist_enemy.npcId,
      enemyNpcLevelId: this.duelist_enemy.npcLevelId,
    });

    if (duelist !== this.duelists[0]) {
      // Give the player some rewards.
      EventBus.emit(GAME_EVENTS.PLAYER_WON_DUEL);

      // Track player won duel event
      trackMixpanelEvent('Player Won Duel', {
        enemyNpcId: this.duelist_enemy.npcId,
        enemyNpcLevelId: this.duelist_enemy.npcLevelId,
      });
    }
  }

  async handleDuelistPerformedRiposte(payload: {
    duelistIndex: number;
    word: string;
  }) {
    const { duelistIndex, word } = payload;
    await this.castSpell(duelistIndex, word);
    for (var i = 0; i < this.duelists.length; i++) {
      if (i !== duelistIndex) {
        this.duelists[i].modifyCurrentHP(-1);
      }
    }
  }

  async handleNewDuelWordStarted() {
    const duelUI = this.getDuelUI();
    // Hide for now, can bring back referee later if we want.
    // duelUI.refereeContainer.setVisible(true);
    const refOrigin = duelUI.refereeImage.x;
    duelUI.refereeImage.alpha = 0;
    // Move tween
    this.tweens.add({
      targets: duelUI.refereeImage,
      x: refOrigin - 75,
      duration: 800,
      ease: 'Linear',
    });
    // Fade in tween.
    this.tweens.add({
      targets: duelUI.refereeImage,
      alpha: 1,
      duration: 100,
      ease: 'Linear',
    });
    // Fade out tween.
    this.tweens.add({
      targets: duelUI.refereeImage,
      alpha: 0,
      duration: 100,
      delay: 700,
      ease: 'Linear',
      onComplete: () => {
        duelUI.refereeImage.alpha = 0;
        duelUI.refereeImage.x = refOrigin;
      },
    });

    const wordCreationPanel = this.getWordCreationPanel();
    if (!wordCreationPanel) {
      console.error('No word creation panel!');
      return;
    }
    if (!wordCreationPanel.currentTargetWord) {
      console.error('No current target word!');
      return;
    }
    wordCreationPanel.setBoardInteractive(this.activeDuelistIndex === 0);
    await playWordInSentence(wordCreationPanel.currentTargetWord);
    await waitForSeconds(this.time, 0.2);
    if (this.activeDuelistIndex === 0) {
      // The player started a new round.
      EventBus.emit(GAME_EVENTS.STUDENT_GAME_ROUND_STARTED);
    }
    duelUI.refereeContainer.setVisible(false);
  }

  handlePlayerQuitDuel() {
    // Track player quit duel event
    trackMixpanelEvent('Player Quit Duel', {
      enemyNpcId: this.duelist_enemy.npcId,
      enemyNpcLevelId: this.duelist_enemy.npcLevelId,
      duelIsFinished: this.duelIsFinished,
    });
  }

  handlePlayerRequestedDuelWalkthrough() {
    // Track help button clicked event
    trackMixpanelEvent('Duel Help Button Clicked', {
      enemyNpcId: this.duelist_enemy.npcId,
      enemyNpcLevelId: this.duelist_enemy.npcLevelId,
    });
  }

  handleSettingsPanelClosed() {
    if (getSceneManager()?.activeSceneKey === 'Duel') {
      getSceneManager()?.resumeScene('Duel');
    }
  }

  handlePrerender() {
    if (!this.duelists) return;
    const activeDuelist = this.duelists[this.activeDuelistIndex];
    if (!activeDuelist) return;
    this.turnIndicator.setPosition(
      activeDuelist.avatar.x,
      activeDuelist.avatar.y,
    );
  }

  returnToLevelMap() {
    getSceneManager()?.launchScene('LevelMap', LevelMap, {
      type: 'gameplay',
      stopActiveScene: true,
    });
  }

  shutdown() {
    this.sound.stopAll();
    this.duelists = [];
    this.activeDuelistIndex = -1;
    this.duelIsFinished = false;

    EventBus.emit(GAME_EVENTS.DUEL_ENDED);

    // Track duel ended event
    trackMixpanelEvent('Duel Ended', {
      duelIsFinished: this.duelIsFinished,
      playerWon: this.duelists[0] && !this.duelists[0].isDefeated,
    });

    waitForSeconds(this.time, 2).then(() => {
      RewardsManager.CheckRewards();
    });
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
